/**
 * PATH: packages/email-hub/src/feature/lib/quoteSelection.ts
 * FILE_ID: EMAILHUB-LIB-QUOTESELECTION-001
 * PURPOSE: Highlight-to-quote hand-off — carry the operator's text
 *          selection from the reader into the reply scaffold so the reply
 *          quotes JUST the highlighted passage instead of the whole chain.
 * WHY:     FEAT-2026-07-06-COMPOSER-MICRO §highlight-to-quote (the Gmail
 *          "quote selected text" power feature). Point replies to one
 *          question in a long email are the norm in construction threads.
 * FLOW:
 *   EmailReader Reply click ──captureQuoteSelection──▶ pending {emailId, html}
 *   useComposePage prefill  ──takePendingQuoteSelection──▶ html | null
 *   (consume-once: a later scaffold rebuild falls back to the full chain)
 * SAFETY: The captured HTML is fed through the SAME sanitizeEmailHtml the
 *         full-body quote uses — this module only transports it.
 */

let pending: { emailId: string; html: string } | null = null

/**
 * Snapshot the current window selection as the quote seed for `emailId`.
 * Returns true when a usable selection was captured. No-ops (false) when
 * the selection is collapsed, outside `container` (when given), or too
 * short to be a deliberate quote.
 */
export function captureQuoteSelection(
  emailId: string,
  container?: Element | null,
): boolean {
  try {
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return false
    const range = sel.getRangeAt(0)
    if (container && !container.contains(range.commonAncestorContainer)) return false
    const div = document.createElement('div')
    div.appendChild(range.cloneContents())
    const text = (div.textContent || '').trim()
    // A twitchy 1-2 char swipe is noise, not a quote intent.
    if (text.length < 3) return false
    pending = { emailId, html: div.innerHTML }
    return true
  } catch {
    return false
  }
}

/** Consume the pending selection for this email (once). */
export function takePendingQuoteSelection(emailId: string): string | null {
  if (!pending || pending.emailId !== emailId) return null
  const html = pending.html
  pending = null
  return html
}

/** Test/cleanup hook. */
export function clearQuoteSelection(): void {
  pending = null
}
